import React, { useState, useEffect } from "react";
import { X, Save, Eye, Upload, Tag, Calendar, User, FileText } from "lucide-react";
import { useContentManagementContext } from "../ContentManagementContext";

const getDefaultFormState = () => ({
  title: "",
  content: "",
  excerpt: "",
  status: "draft",
  category: "",
  tags: [],
  isFeatured: false,
  featuredImage: {
    url: "",
    publicId: "",
    alt: ""
  }
});

const CreateBlogModal = ({ isOpen, onClose, mode = "create", blogId = null }) => {
  const { createBlogPost, updateBlogPost, fetchBlogPost, loading } = useContentManagementContext();
  const isEditMode = mode === "edit";

  const [formData, setFormData] = useState(getDefaultFormState);
  const [tagInput, setTagInput] = useState("");
  const [errors, setErrors] = useState({});
  const [previewMode, setPreviewMode] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [initialError, setInitialError] = useState(null);

  const mapBlogToFormData = (blog = {}) => ({
    title: blog.title || "",
    content: blog.content || "",
    excerpt: blog.excerpt || "",
    status: (blog.status?.name || blog.status || "draft").toLowerCase(),
    category: blog.category || "",
    tags: Array.isArray(blog.tags) ? blog.tags : blog.tags ? [blog.tags] : [],
    isFeatured: Boolean(blog.isFeatured),
    featuredImage: {
      url: blog.featuredImage?.url || "",
      publicId: blog.featuredImage?.publicId || "",
      alt: blog.featuredImage?.alt || ""
    }
  });

  useEffect(() => {
    if (!isOpen) {
      setFormData(getDefaultFormState());
      setTagInput("");
      setErrors({});
      setPreviewMode(false);
      setInitialError(null);
      setInitializing(false);
      return;
    }

    if (isEditMode) {
      if (!blogId) {
        setInitialError("Unable to edit blog: missing identifier.");
        return;
      }

      const loadBlog = async () => {
        setInitializing(true);
        setInitialError(null);
        try {
          const blog = await fetchBlogPost(blogId);
          setFormData(mapBlogToFormData(blog));
          setTagInput("");
          setErrors({});
          setPreviewMode(false);
        } catch (error) {
          setInitialError(error.message || "Failed to load blog details.");
        } finally {
          setInitializing(false);
        }
      };

      loadBlog();
    } else {
      setFormData(getDefaultFormState());
    }
  }, [isOpen, isEditMode, blogId, fetchBlogPost]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "featuredImage.url" || name === "featuredImage.alt") {
      setFormData(prev => ({
        ...prev,
        featuredImage: {
          ...prev.featuredImage,
          [name.split('.')[1]]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  // Handle tag addition
  const handleAddTag = (e) => {
    if (e.key === "Enter" && tagInput.trim()) {
      e.preventDefault();
      const newTag = tagInput.trim().toLowerCase();
      if (!formData.tags.includes(newTag)) {
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, newTag]
        }));
      }
      setTagInput("");
    }
  };

  // Handle tag removal
  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.content.trim()) {
      newErrors.content = "Content is required";
    }

    if (formData.title.length > 200) {
      newErrors.title = "Title must be less than 200 characters";
    }

    if (formData.excerpt.length > 500) {
      newErrors.excerpt = "Excerpt must be less than 500 characters";
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (isEditMode) {
        if (!blogId) {
          setInitialError("Unable to save changes: missing blog identifier.");
          return;
        }
        await updateBlogPost(blogId, formData);
      } else {
        await createBlogPost(formData);
      }
      handleClose();
    } catch (error) {
      console.error("Failed to submit blog post:", error);
      setInitialError(error.message || "Failed to submit blog post.");
    }
  };

  // Handle modal close
  const handleClose = () => {
    setFormData(getDefaultFormState());
    setTagInput("");
    setErrors({});
    setPreviewMode(false);
    setInitialError(null);
    setInitializing(false);
    onClose();
  };

  // Handle title change
  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title
    }));
  };

  if (!isOpen) return null;

  const modalTitle = isEditMode ? "Edit Blog Post" : "Create New Blog Post";
  const primaryActionLabel = isEditMode ? "Save Changes" : "Create Blog Post";
  const primaryActionLoadingLabel = isEditMode ? "Saving..." : "Creating...";
  const isSubmitDisabled = loading || (isEditMode && initializing);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] flex flex-col">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">{modalTitle}</h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isEditMode && initializing ? (
          <div className="flex flex-1 items-center justify-center p-6">
            <div className="flex flex-col items-center space-y-3 text-gray-600">
              <div className="w-10 h-10 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
              <p>Loading blog details...</p>
            </div>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400">
              {initialError && (
                <div className="p-3 border border-red-200 bg-red-50 rounded text-sm text-red-700">
                  {initialError}
                </div>
              )}
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleTitleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${errors.title ? "border-red-300" : "border-gray-300"
                    }`}
                  placeholder="Enter blog post title..."
                  maxLength={200}
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {formData.title.length}/200 characters
                </p>
              </div>

              {/* Content */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Content *
                  </label>
                  <button
                    type="button"
                    onClick={() => setPreviewMode(!previewMode)}
                    className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-800"
                  >
                    <Eye className="w-4 h-4" />
                    <span>{previewMode ? "Edit" : "Preview"}</span>
                  </button>
                </div>

                {previewMode ? (
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md min-h-[200px] bg-gray-50">
                    <div className="prose max-w-none">
                      <h1 className="text-2xl font-bold mb-4">{formData.title}</h1>
                      <div dangerouslySetInnerHTML={{ __html: formData.content.replace(/\n/g, '<br>') }} />
                    </div>
                  </div>
                ) : (
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${errors.content ? "border-red-300" : "border-gray-300"
                      }`}
                    placeholder="Write your blog post content here..."
                    rows={10}
                  />
                )}
                {errors.content && (
                  <p className="mt-1 text-sm text-red-600">{errors.content}</p>
                )}
              </div>

              {/* Excerpt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Excerpt
                </label>
                <textarea
                  name="excerpt"
                  value={formData.excerpt}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${errors.excerpt ? "border-red-300" : "border-gray-300"
                    }`}
                  placeholder="Brief description of the blog post..."
                  rows={3}
                  maxLength={500}
                />
                {errors.excerpt && (
                  <p className="mt-1 text-sm text-red-600">{errors.excerpt}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {formData.excerpt.length}/500 characters
                </p>
              </div>

              {/* Category and Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <input
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="e.g., Tutorial, News, Guide"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-1 text-green-600 hover:text-green-800"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={handleAddTag}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Type a tag and press Enter..."
                />
              </div>

              {/* Featured Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Featured Image
                </label>
                <div className="space-y-3">
                  <input
                    type="url"
                    name="featuredImage.url"
                    value={formData.featuredImage.url}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Image URL..."
                  />
                  <input
                    type="text"
                    name="featuredImage.alt"
                    value={formData.featuredImage.alt}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Alt text for the image..."
                  />
                </div>
              </div>


              {/* Featured Post */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isFeatured"
                  checked={formData.isFeatured}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  Mark as featured post
                </label>
              </div>
            </div>

            {/* Footer - Fixed */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitDisabled}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>{primaryActionLoadingLabel}</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>{primaryActionLabel}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateBlogModal;
